import { Routes, Route, NavLink, useLocation, Navigate, Link } from 'react-router-dom';
import EngineRoom from './routes/EngineRoom';
import DriftPacket from './routes/DriftPacket';
import DriftList from './routes/DriftList';
import AlertDetail from './routes/AlertDetail';
import WarGame from './routes/WarGame';
import Scoring from './routes/Scoring';
import Spine from './routes/Spine';
import SignalDataRegistry from './routes/SignalDataRegistry';
import Ingest from './routes/Ingest';
import LearningLoop from './routes/LearningLoop';
import OpportunityLedger from './routes/OpportunityLedger';
import Memory from './routes/Memory';
import Briefing from './routes/Briefing';
import Synthesis from './routes/Synthesis';
import Emerging from './routes/Emerging';
import Ask from './routes/Ask';
import Cascade from './routes/Cascade';
import Validation from './routes/Validation';
import Login from './routes/Login';
import Engine1Shell from './routes/Engine1Shell';
import Engine2Shell from './routes/Engine2Shell';
import Engine3Shell from './routes/Engine3Shell';
import Engine4Shell from './routes/Engine4Shell';
import Engine5Shell from './routes/Engine5Shell';
import Engine6Shell from './routes/Engine6Shell';
import Engine7Shell from './routes/Engine7Shell';
import AdminShell from './routes/admin/AdminShell';
import AdminHome from './routes/admin/AdminHome';
import AdminSystem from './routes/admin/System';
import AdminIntegrations from './routes/admin/Integrations';
import AdminUsers from './routes/admin/Users';
import AdminOwners from './routes/admin/Owners';
import AdminAudit from './routes/admin/Audit';
import AdminMethodRegistry from './routes/admin/MethodRegistry';
import AdminDataFeeds from './routes/admin/DataFeeds';
import AdminDemo from './routes/admin/Demo';
import AdminEngines from './routes/admin/Engines';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import AskBar from './components/AskBar';
import { C2_EVOLUTION_ENABLED } from './evolutionFlag';

// Intent-based information architecture: groups by what the operator is trying to DO, not by the
// backend's engine taxonomy. (Cascade Trace + Validation Evidence are no longer nav items — they're
// embedded in context: Cascade in the drift packet + Northstar apex card, Validation in memory.
// Signal-to-Spine Map + Data Connectors are merged into one "Signal & Data Registry" item.)
const NAV = [
  { grp: 'Command Center' as const },
  { id: '/',           ico: '▦', t: 'Engine Room',        end: true },
  { id: '/ask',        ico: '?', t: 'Ask the System' },
  { id: '/briefing',   ico: '▤', t: 'Standing Briefing' },
  { id: '/drift',      ico: '◇', t: 'Drift Packets' },
  { id: '/synthesis',  ico: '⊕', t: 'Cross-Engine Synthesis' },
  { id: '/emerging',   ico: '◌', t: 'Emerging & Pre-Breach' },
  { grp: 'Engines' as const },
  { id: '/engine/1',   ico: '⚗', t: '1 · Innovation' },
  { id: '/engine/2',   ico: '⛓', t: '2 · Sourcing' },
  { id: '/engine/3',   ico: '⚙', t: '3 · Manufacturing' },
  { id: '/engine/4',   ico: '⇄', t: '4 · Distribution' },
  { id: '/engine/5',   ico: '✦', t: '5 · Go-to-Market' },
  { id: '/engine/6',   ico: '⚔', t: '6 · Simulation' },
  { id: '/engine/7',   ico: '⌬', t: '7 · Corp Dev / M&A' },
  { grp: 'Strategy & Pipeline' as const },
  { id: '/spine',      ico: '∥', t: 'Strategic Spine' },
  { id: '/signals',    ico: '→', t: 'Signal & Data Registry', match: ['/connectors'] },
  { id: '/ingest',     ico: '⊞', t: 'Text Ingestion' },
  { id: '/scoring',    ico: '⚖', t: 'Spine-Anchored Scoring' },
  { id: '/opportunities', ico: '◎', t: 'Opportunity Ledger' },
  { grp: 'Review & Learning' as const },
  { id: '/memory',     ico: '◷', t: 'Institutional Memory' },
  { id: '/learning',   ico: '↻', t: 'Learning Loop' },
];

function Sidebar() {
  const location = useLocation();
  const { user } = useAuth();
  const isActive = (item: { id: string; end?: boolean; match?: string[] }) =>
    item.end
      ? location.pathname === item.id
      : location.pathname.startsWith(item.id) || (item.match ?? []).some((m) => location.pathname.startsWith(m));

  return (
    <aside className="side">
      <Link to="/" style={{ textDecoration: 'none', borderBottom: 'none' }} aria-label="Home / Engine Room">
        <div className="brand">
          <span className="dot" />
          <h1>AriZona <span className="c2">C2</span></h1>
        </div>
        <div className="brand-sub">Strategy Control Surface</div>
      </Link>
      <nav className="nav">
        {NAV.map((item, i) =>
          'grp' in item ? (
            <div className="nav-grp" key={`grp-${i}`}>{item.grp}</div>
          ) : (
            <NavLink
              key={item.id}
              to={item.id}
              end={item.end}
              className={isActive(item) ? 'on' : ''}
            >
              <span className="ico">{item.ico}</span>
              {item.t}
            </NavLink>
          ),
        )}
        {user?.is_admin && (
          <>
            <div className="nav-grp">Admin</div>
            <NavLink to="/admin" className={location.pathname.startsWith('/admin') ? 'on' : ''}>
              <span className="ico">⚙</span>
              Admin
            </NavLink>
          </>
        )}
      </nav>
      <div className="side-foot">
        Seeded fictional demo.
        <br />
        Reads from <span className="mono">/api</span> live.
        <br />
        <span className="mono">v0.2.0</span>
      </div>
    </aside>
  );
}

function TopBar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();

  return (
    <div className="topbar">
      <div className="constraint" title="System-wide constraint, shown on every screen">
        <span className="pulse" />
        <span className="constraint-tag">System-wide</span>
        Current constraint:&nbsp;<b>Sourcing and Supply</b>&nbsp;(can supply), week 3
      </div>
      <div className="spacer" />
      <AskBar />
      <button
        type="button"
        className="btn-icon"
        onClick={toggle}
        aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
        title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
      >
        {theme === 'dark' ? '☀' : '☾'}
      </button>
      <span className="ver">api/health · ok</span>
      {user && (
        <button type="button" className="btn ghost" onClick={logout} style={{ marginLeft: 8 }}>
          ⏻ {user.email.split('@')[0]}
        </button>
      )}
    </div>
  );
}

function Shell() {
  return (
    <div className="shell">
      <Sidebar />
      <div className="main">
        <TopBar />
        <div className="content">
          <Routes>
            <Route path="/" element={<EngineRoom />} />
            <Route path="/drift" element={<DriftList />} />
            <Route path="/drift/:id" element={<DriftPacket />} />
            <Route path="/alert/:id" element={<AlertDetail />} />
            <Route path="/wargame" element={<WarGame />} />
            <Route path="/scoring" element={<Scoring />} />
            <Route path="/spine" element={<Spine />} />
            {/* Merged Signal & Data Registry — both paths render the tabbed page so deep links survive. */}
            <Route path="/signals" element={<SignalDataRegistry />} />
            <Route path="/connectors" element={<SignalDataRegistry />} />
            <Route path="/ingest" element={<Ingest />} />
            <Route path="/learning" element={<LearningLoop />} />
            <Route path="/ask" element={<Ask />} />
            <Route path="/synthesis" element={<Synthesis />} />
            <Route path="/emerging" element={<Emerging />} />
            <Route path="/opportunities" element={<OpportunityLedger />} />
            <Route path="/memory" element={<Memory />} />
            <Route path="/briefing" element={<Briefing />} />
            {C2_EVOLUTION_ENABLED && (
              <>
                <Route path="/cascade" element={<Cascade />} />
                <Route path="/validation" element={<Validation />} />
              </>
            )}
            <Route path="/engine/1" element={<Engine1Shell />} />
            <Route path="/engine/2" element={<Engine2Shell />} />
            <Route path="/engine/3" element={<Engine3Shell />} />
            <Route path="/engine/4" element={<Engine4Shell />} />
            <Route path="/engine/5" element={<Engine5Shell />} />
            <Route path="/engine/6" element={<Engine6Shell />} />
            <Route path="/engine/7" element={<Engine7Shell />} />
            <Route path="/admin" element={<AdminShell />}>
              <Route index element={<AdminHome />} />
              <Route path="system" element={<AdminSystem />} />
              <Route path="integrations" element={<AdminIntegrations />} />
              <Route path="data-feeds" element={<AdminDataFeeds />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="owners" element={<AdminOwners />} />
              <Route path="audit" element={<AdminAudit />} />
              <Route path="method-registry" element={<AdminMethodRegistry />} />
              <Route path="demo" element={<AdminDemo />} />
              <Route path="engines" element={<AdminEngines />} />
            </Route>
          </Routes>
        </div>
      </div>
    </div>
  );
}

function AuthGate({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="login-wrap">
        <div className="cond" style={{ color: 'var(--muted)', letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: 12 }}>
          Loading session…
        </div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/*"
            element={
              <AuthGate>
                <Shell />
              </AuthGate>
            }
          />
        </Routes>
      </AuthProvider>
    </ThemeProvider>
  );
}
